/**
 * App — Router and top-level context providers
 */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './hooks/useAuth';
import { useCart } from './hooks/useCart';
import React, { createContext, useContext, useState, useEffect } from 'react';

// Pages
import LandingPage        from './pages/LandingPage';
import ShopPage           from './pages/ShopPage';
import ProductPage        from './pages/ProductPage';
import CartPage           from './pages/CartPage';
import CheckoutPage       from './pages/CheckoutPage';
import OrderSuccessPage   from './pages/OrderSuccessPage';
import CryptoPaymentPage  from './pages/CryptoPaymentPage';
import CardPaymentPage    from './pages/CardPaymentPage';
import NmiPaymentPage     from './pages/NmiPaymentPage';
import PaymentReturnPage  from './pages/PaymentReturnPage';
import ThankYouPage       from './pages/ThankYouPage';
import LoginPage          from './pages/LoginPage';
import RegisterPage       from './pages/RegisterPage';
import AccountPage        from './pages/AccountPage';
import MembershipPage      from './pages/MembershipPage';
import MembershipReturnPage from './pages/MembershipReturnPage';
import DashboardPage      from './pages/DashboardPage';
import AdminPage          from './pages/AdminPage';
import NotFoundPage       from './pages/NotFoundPage';
import VerifyEmailPage    from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AboutPage          from './pages/AboutPage';
import SupportPage        from './pages/SupportPage';
import COAPage            from './pages/COAPage';
import TermsPage          from './pages/TermsPage';
import PrivacyPage        from './pages/PrivacyPage';
import CompliancePage     from './pages/CompliancePage';
import UnsubscribePage    from './pages/UnsubscribePage';
import LegalGateModal      from './components/LegalGateModal';
import MaintenanceGate      from './components/MaintenanceGate';
import AnalyticsTracker     from './components/AnalyticsTracker';
import SocialProofPopup     from './components/SocialProofPopup';
import LiveChatWidget       from './components/LiveChatWidget';
import ScrollToTop          from './components/ScrollToTop';

// ─── Auth + Cart + DarkMode contexts ────────────────────────────────────────
export const AuthContext     = createContext(null);
export const CartContext     = createContext(null);
export const DarkModeContext = createContext(null);
export const useAuthCtx     = () => useContext(AuthContext);
export const useCartCtx     = () => useContext(CartContext);
export const useDarkMode    = () => useContext(DarkModeContext);

/** Protected route wrapper */
const ProtectedRoute = ({ children, roles }) => {
  const { user } = useAuthCtx();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const DashboardOrAdminRedirect = () => {
  const { user } = useAuthCtx();
  if (user?.role === 'admin') return <Navigate to="/admin/users" replace />;
  return <DashboardPage />;
};

export default function App() {
  const auth = useAuth();
  const cart = useCart();

  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('truetide-dark') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    try { localStorage.setItem('truetide-dark', String(darkMode)); } catch {}
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
    <AuthContext.Provider value={auth}>
      <CartContext.Provider value={cart}>
        <BrowserRouter>
          {/* Snap to the top of the page on every navigation */}
          <ScrollToTop />
          {/* First-party pageview / live-user tracking (storefront only) */}
          <AnalyticsTracker />
          {/* First thing every visitor sees — blocks the site until accepted */}
          <LegalGateModal />
          {/* Branded "we'll be back shortly" page when the admin pauses the site */}
          <MaintenanceGate />
          {/* Storefront "recent orders" social-proof popup (hidden on /admin) */}
          <SocialProofPopup />
          {/* Site-wide live-chat launcher, bottom-right (hidden on /admin) */}
          <LiveChatWidget />
          <Routes>
            {/* Public */}
            <Route path="/"                element={<LandingPage />} />
            <Route path="/shop" element={
              <ProtectedRoute><ShopPage /></ProtectedRoute>
            } />
            <Route path="/shop/:slug" element={
              <ProtectedRoute><ProductPage /></ProtectedRoute>
            } />
            <Route path="/login"           element={<LoginPage />} />
            <Route path="/register"        element={<RegisterPage />} />
            <Route path="/verify-email"    element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/about"           element={<AboutPage />} />
            <Route path="/support"         element={<SupportPage />} />
            <Route path="/coa"             element={<COAPage />} />
            <Route path="/terms"           element={<TermsPage />} />
            <Route path="/privacy"         element={<PrivacyPage />} />
            <Route path="/compliance"      element={<CompliancePage />} />
            <Route path="/unsubscribe"     element={<UnsubscribePage />} />

            {/* Customer */}
            <Route path="/membership" element={
              <ProtectedRoute><MembershipPage /></ProtectedRoute>
            } />
            <Route path="/cart"     element={<CartPage />} />
            <Route path="/checkout" element={
              <ProtectedRoute><CheckoutPage /></ProtectedRoute>
            } />
            <Route path="/order-success/:id" element={
              <ProtectedRoute><OrderSuccessPage /></ProtectedRoute>
            } />
            <Route path="/crypto-payment/:id" element={
              <ProtectedRoute><CryptoPaymentPage /></ProtectedRoute>
            } />
            <Route path="/card-payment/:id" element={
              <ProtectedRoute><CardPaymentPage /></ProtectedRoute>
            } />
            <Route path="/pay/:id" element={
              <ProtectedRoute><NmiPaymentPage /></ProtectedRoute>
            } />
            <Route path="/payment/return/:id" element={
              <ProtectedRoute><PaymentReturnPage /></ProtectedRoute>
            } />
            <Route path="/membership/return/:id" element={
              <ProtectedRoute><MembershipReturnPage /></ProtectedRoute>
            } />
            <Route path="/thank-you/:id" element={
              <ProtectedRoute><ThankYouPage /></ProtectedRoute>
            } />
            <Route path="/account" element={
              <ProtectedRoute><AccountPage /></ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute><DashboardOrAdminRedirect /></ProtectedRoute>
            } />

            {/* Admin */}
            <Route path="/admin/*" element={
              <ProtectedRoute roles={['admin']}><AdminPage /></ProtectedRoute>
            } />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>

          <ToastContainer position="bottom-right" autoClose={4000} />
        </BrowserRouter>
      </CartContext.Provider>
    </AuthContext.Provider>
    </DarkModeContext.Provider>
  );
}
